const STORAGE_KEY = "ad_click_id";
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
const CLICK_KEYS = ["gclid", "gbraid", "wbraid"] as const;

export type AdClickType = (typeof CLICK_KEYS)[number];

export type StoredAdClick = {
  type: AdClickType;
  value: string;
  capturedAt: number;
};

export type AdClickAttribution = {
  click_id_type: AdClickType;
  click_id_value: string;
  click_id_at: number;
};

const isClickType = (value: string): value is AdClickType =>
  CLICK_KEYS.includes(value as AdClickType);

const writeClick = (type: AdClickType, value: string) => {
  const payload: StoredAdClick = {
    type,
    value,
    capturedAt: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

/**
 * Last-click capture. A new URL id overwrites storage.
 * The Google cookie is only used when nothing is stored yet.
 */
export const captureAdClickId = () => {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  for (const key of CLICK_KEYS) {
    const value = params.get(key);
    if (value) {
      writeClick(key, value);
      return;
    }
  }

  if (localStorage.getItem(STORAGE_KEY)) return;

  const match = document.cookie.match(/(?:^|;\s*)_gcl_aw=([^;]+)/);
  if (!match?.[1]) return;

  const parts = decodeURIComponent(match[1]).split(".");
  if (parts.length < 3) return;
  const value = parts.slice(2).join(".");
  if (!value) return;
  writeClick("gclid", value);
};

export const getAdClickId = (): StoredAdClick | null => {
  if (typeof window === "undefined") return null;

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const data = JSON.parse(raw) as Partial<StoredAdClick>;
    if (!data.type || !isClickType(data.type) || !data.value || !data.capturedAt) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (Date.now() - data.capturedAt > NINETY_DAYS_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return {
      type: data.type,
      value: data.value,
      capturedAt: data.capturedAt,
    };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const getAdClickAttribution = (): AdClickAttribution | null => {
  const click = getAdClickId();
  if (!click) return null;
  return {
    click_id_type: click.type,
    click_id_value: click.value,
    click_id_at: click.capturedAt,
  };
};
